import { parseNumber, parseNumberToStringWithSeparator } from '@/src/util/numbers';

import { TaxRate, LineEntry } from '../../types';
import { LineEntryForm } from '../../types/treatment';

export const calculateTaxRate = (rates: TaxRate[]) => {
  return rates.reduce((accumulator, currentValue) => {
    return Number(currentValue.rate) + accumulator;
  }, 0);
};

export const TaxCalculationEnum = {
  TaxExcluded: 'TaxExcluded',
  TaxInclusive: 'TaxInclusive',
  NotApplicable: 'NotApplicable',
};

export const TaxCalculationEnumMap = {
  ['Exclusive of Tax']: TaxCalculationEnum.TaxExcluded,
  ['Inclusive of Tax']: TaxCalculationEnum.TaxInclusive,
  ['Out of Scope']: TaxCalculationEnum.NotApplicable,
};

export const TaxCalculationMap = {
  [TaxCalculationEnum.TaxExcluded]: 'Exclusive of Tax',
  [TaxCalculationEnum.TaxInclusive]: 'Inclusive of Tax',
  [TaxCalculationEnum.NotApplicable]: 'Out of Scope',
};

export type Strategy = {
  getTaxRate?: (taxCode?: LineEntryForm['taxCode'] | LineEntry['taxCode']) => number;
  calculateTaxableAmount?: (amount: number, taxRate: number) => number;
  calculateSubtotalDisplay?: (amount: number, tax: number) => number;
};

export class TreatmentCalculator {
  static formatTotals({
    subTotal,
    taxesSubTotal,
    total,
  }: {
    subTotal: number;
    taxesSubTotal: number;
    total: number;
  }) {
    return {
      subTotal: parseNumberToStringWithSeparator(subTotal, 2),
      taxesSubTotal: parseNumberToStringWithSeparator(taxesSubTotal, 2),
      total: parseNumberToStringWithSeparator(total, 2),
    };
  }

  static getTaxRate(taxCode?: LineEntry['taxCode'] | LineEntryForm['taxCode']) {
    if (!taxCode) {
      return 0;
    }

    return taxCode.taxRate;
  }

  static getAmount(amount: number) {
    return amount;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static calculateTaxableAmount(amount: number, taxRate: number) {
    return TreatmentCalculator.getAmount(amount);
  }

  static calculateAmount(price: number, quantity: number) {
    return (price * quantity).toFixed(2);
  }

  static calculateTax = (amount: number, taxRate: number) => {
    return amount * (taxRate / 100);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static calculateSubtotalDisplay = (amount: number, tax: number) => {
    return amount;
  };

  static calculateTotals({
    lineItems,
    strategy = {
      getTaxRate: this.getTaxRate,
      calculateTaxableAmount: this.getAmount,
      calculateSubtotalDisplay: this.calculateSubtotalDisplay,
    },
  }: {
    lineItems: LineEntry[] | LineEntryForm[];
    strategy?: Strategy;
  }) {
    let taxesSubTotal = 0;
    let subTotal = 0;
    let total = 0;

    for (const lineItem of lineItems) {
      const amount = parseNumber(lineItem.totalAmount || 0);
      const taxRate = strategy.getTaxRate(lineItem.taxCode);
      const taxableAmount = strategy.calculateTaxableAmount(amount, taxRate);
      const tax = this.calculateTax(taxableAmount, taxRate);

      subTotal += strategy.calculateSubtotalDisplay(taxableAmount, tax);
      taxesSubTotal += tax;
      total += taxableAmount + tax;
    }

    return this.formatTotals({ subTotal, taxesSubTotal, total });
  }

  static calculateLineItems({
    lineItems,
    strategy = {
      getTaxRate: this.getTaxRate,
      calculateTaxableAmount: this.getAmount,
      calculateSubtotalDisplay: this.calculateSubtotalDisplay,
    },
  }: {
    lineItems: LineEntryForm[] | LineEntry[];
    usePriceBeforeTax?: boolean;
    strategy?: Strategy;
  }) {
    return lineItems.map((lineItem) => {
      const amount = parseNumber(lineItem.totalAmount || 0);
      const taxRate = strategy.getTaxRate(lineItem.taxCode);
      const taxableAmount = strategy.calculateTaxableAmount(amount, taxRate);
      return {
        ...lineItem,
        totalAmountBeforeTax: amount,
        totalAmountAfterTax: taxableAmount,
        totalAmount: taxableAmount,
      };
    });
  }

  static calculateLineItemsAndTotals({
    lineItems,
    strategy = {
      getTaxRate: this.getTaxRate,
      calculateTaxableAmount: this.calculateTaxableAmount,
      calculateSubtotalDisplay: this.calculateSubtotalDisplay,
    },
  }: {
    lineItems: LineEntryForm[] | LineEntry[];
    usePriceBeforeTax?: boolean;
    strategy?: Strategy;
  }) {
    const strategies = {
      getTaxRate: this.getTaxRate,
      calculateTaxableAmount: this.calculateTaxableAmount,
      calculateSubtotalDisplay: this.calculateSubtotalDisplay,
      ...strategy,
    };

    return {
      lineItems: this.calculateLineItems({ lineItems, strategy: strategies }),
      totals: this.calculateTotals({ lineItems, strategy: strategies }),
    };
  }

  // QB Publishing calculation
  static recalculateLineItemsAmount(lineItems: LineEntryForm[] | LineEntry[]) {
    return lineItems;
  }
}

// export const calculateTaxedAmountFromItemSetting = ({ item, dropdowns, taxCalculation }) => {
//   if (item.taxIncluded && item.price && item.taxCode) {
//       const taxCode = dropdowns.taxCodes.find(taxCode => taxCode.value === item.taxCode.value);

//       return calculateTaxedAmount(item.price, taxCode.taxRate, taxCalculation);
//   }
//   return item.price;
// }
